import React from 'react';
import PropTypes from 'prop-types';
import {Flex, Box, Grid, Text, Divider} from '@spothero/ui';
import FormatUtils from 'utils/format';
import {
    PROPTYPE_RATE_DIFF,
    PROPTYPE_RESERVATION_VEHICLE,
} from '@/proptypes/reservation-prop-types';
import PaymentPicker, {ADD_NEW_CARD_VALUE} from 'common/payment-picker';
import {updateVehicle} from 'store/parking-pass/parking-pass-actions';
import {useDispatch, useSelector} from 'react-redux';
import {refreshUser} from 'store/user/user-actions';
import UserUtils from 'utils/user-utils';

const textStyle = {
    fontSize: 'sm',
    lineHeight: '1.4',
};

const PayOversize = ({
    rateDiff,
    vehicle,
    control,
    handleSubmit,
    setError,
    errors,
    reservationId,
    handleSuccess,
    handleReservationVehicleUpdated,
    handleReservationVehicleUpdateFailed,
}) => {
    const dispatch = useDispatch();
    const {accessKey} = useSelector(state => state.parkingPass);
    const {status: userStatus} = useSelector(state => state.user.data);

    const {
        priceBreakdown: {chargeAmount, price},
        originalRental: {price: originalRentalPrice},
        priceDifference,
    } = rateDiff;

    const oversizeFee = FormatUtils.price(
        chargeAmount.charge + chargeAmount.credit
    ).displayPrice;
    const originalPrice = FormatUtils.price(originalRentalPrice).displayPrice;
    const totalPrice = FormatUtils.price(price).displayPrice;
    const chargedToCard = FormatUtils.price(chargeAmount.charge).displayPrice;
    const chargedToCredit = FormatUtils.price(chargeAmount.credit).displayPrice;

    const onSubmitCard = ({cardValue, stripeToken}) => {
        return dispatch(
            updateVehicle({
                vehicleProfileId: vehicle.vehicleProfileId,
                reservationId,
                price,
                cardExternalId: cardValue,
                stripeToken,
                guestVehicle:
                    userStatus !== UserUtils.AUTH_STATE.USER ? vehicle : null,
                accessKey,
            })
        );
    };

    const onSuccess = ({cardValue}) => {
        handleReservationVehicleUpdated({
            priceDifference,
            newVehicle: vehicle,
            view: 'pay-oversize',
        });

        handleSuccess(chargeAmount.charge + chargeAmount.credit);
        if (
            cardValue === ADD_NEW_CARD_VALUE &&
            userStatus === UserUtils.AUTH_STATE.USER
        ) {
            try {
                dispatch(refreshUser());
            } catch {} // eslint-disable-line no-empty
        }
    };

    const onFailure = errorMessage => {
        handleReservationVehicleUpdateFailed({
            priceDifference,
            newVehicle: vehicle,
            view: 'pay-oversize',
            errors: [{message: errorMessage}],
        });
    };

    return (
        <Flex width="100%" flexDirection="column">
            <Box
                paddingX={3}
                paddingY={2}
                background="gray.50"
                borderRadius="md"
                marginBottom={6}
            >
                {`This parking facility charges an oversize fee for your vehicle, ${vehicle.makeLabel} ${vehicle.modelLabel}.`}
            </Box>
            <Grid
                gap={2}
                width="100%"
                templateColumns="1fr 1fr"
                marginBottom={6}
            >
                <Text {...textStyle}>Oversize Fee:</Text>
                <Text justifySelf="end" {...textStyle}>
                    ${oversizeFee}
                </Text>
                <Text {...textStyle}>Original Price:</Text>
                <Text justifySelf="end" {...textStyle}>
                    ${originalPrice}
                </Text>
                <Divider gridColumn="1 / 3" />
                <Text {...textStyle}>Total Price:</Text>
                <Text justifySelf="end" {...textStyle}>
                    ${totalPrice}
                </Text>
                {chargeAmount.credit > 0 && (
                    <>
                        <Text {...textStyle}>SpotHero Credit:</Text>
                        <Text justifySelf="end" {...textStyle}>
                            -${chargedToCredit}
                        </Text>
                    </>
                )}
                <Text marginTop={2} {...textStyle} fontWeight="semibold">
                    New amount to be charged:
                </Text>
                <Text
                    marginTop={2}
                    justifySelf="end"
                    {...textStyle}
                    fontWeight="semibold"
                >
                    ${chargedToCard}
                </Text>
            </Grid>
            <PaymentPicker
                control={control}
                errors={errors}
                setError={setError}
                handleSubmit={handleSubmit}
                onSubmitCard={onSubmitCard}
                onSuccess={onSuccess}
                onFailure={onFailure}
            />
        </Flex>
    );
};

PayOversize.propTypes = {
    rateDiff: PROPTYPE_RATE_DIFF,
    vehicle: PROPTYPE_RESERVATION_VEHICLE,
    control: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    errors: PropTypes.object,
    reservationId: PropTypes.number.isRequired,
    handleSuccess: PropTypes.func.isRequired,
    handleReservationVehicleUpdated: PropTypes.func.isRequired,
    handleReservationVehicleUpdateFailed: PropTypes.func.isRequired,
};

export default PayOversize;
